(ns journal-app-client.utils.formatting
  (:require [clojure.string :as s]
            [tick.core :as t]
            [re-frame.core :as rf]
            [goog.string :as gstr]
            [journal-app-client.translations :refer [tr]]))

(defn string->keyword [string]
  (if (string? string)
    (keyword string)
    string))

(defn keyword->string [string]
  (if (keyword? string)
    (name string)
    string))

(defn capitalize [string]
  (when (string? string)
    (s/capitalize string)))

(defn lower-case [string]
  (when (string? string)
    (s/lower-case string)))

(defn upper-case [string]
  (when (string? string)
    (s/upper-case string)))

(defn capitalize-words
  "Capitalize every word in a string"
  [string]
  (when (string? string)
    (->> (s/split (str string) #"\b")
      (map capitalize)
      s/join)))

(defn hyphenated-to-camel-case [string]
  (when (string? string)
    (s/join
      (map
        (fn [word] (s/capitalize word))
        (s/split string #"-")))))

(defn hyphenated->snake-case [string]
  (when (string? string)
    (s/replace string #"-" "_")))

(defn snake-case->hyphenated [string]
  (when (string? string)
    (s/replace string #"_" "-")))

(defn snake-case->kebab-case [string]
  (if (string? string)
    (-> string
      (snake-case->hyphenated)
      (s/lower-case))
    string))

(defn snake-case->kebab-case-key [string]
  (string->keyword (snake-case->kebab-case string)))

(defn map-lisp-keys->snake [map-to-be-transformed]
  (if (map? map-to-be-transformed)
    (reduce-kv
      (fn [m k v]
        (let [string-key (keyword->string k)]
          (if (map? v)
            (assoc m (string->keyword (hyphenated->snake-case string-key)) (map-lisp-keys->snake v))
            (assoc m (string->keyword (hyphenated->snake-case string-key)) v))))
      {}
      map-to-be-transformed)
    map-to-be-transformed))

(defn camel-case->kebab-case [string]
  (if (string? string)
    (-> string
      (s/replace #"(.)([A-Z][a-z]+)" "$1-$2")
      (s/replace #"([a-z0-9])([A-Z])" "$1-$2")
      (s/lower-case))
    string))


(defn map-camel-keys->lisp [map-to-be-transformed]
  (if (map? map-to-be-transformed)
    (reduce-kv
      (fn [m k v]
        (let [string-key (keyword->string k)]
          (if (map? v)
            (assoc m (string->keyword (camel-case->kebab-case string-key)) (map-camel-keys->lisp v))
            (assoc m (string->keyword (camel-case->kebab-case string-key)) v))))
      {}
      map-to-be-transformed)
    map-to-be-transformed))




(defn round-number [f & decimal-points]
  (let [move-decimal (if (first decimal-points)
                       (Math/pow 10 (first decimal-points))
                       10)]
    (/ (Math/round (* move-decimal f)) move-decimal)))

(defn to-seconds [{:keys [hours minutes seconds]}]
  (let [hours-to-seconds (* hours 3600)
        minutes-to-seconds (* minutes 60)]
    (+ hours-to-seconds minutes-to-seconds seconds)))

(defn seconds->minutes [seconds]
  (let [minutes (int (/ seconds 60))
        remaining-seconds (mod seconds 60)]
    [minutes remaining-seconds]))

(defn to-hours [{:keys [minutes seconds]}]
  (let [seconds-in-minutes (seconds->minutes seconds)
        minutes-from-seconds (first seconds-in-minutes)
        remaining-seconds (second seconds-in-minutes)
        total-minutes (+ minutes minutes-from-seconds)
        hours (int (/ total-minutes 60))
        remaining-minutes (mod total-minutes 60)]
    [hours remaining-minutes remaining-seconds]))

(defn readable-time [{:keys [months days hours minutes seconds separator short? include-zero negative-duration?]}]
  (let [negative-text (when negative-duration? "-")
        months-text (if short?
                      (tr {:fallback-phrase "M"} [:time :month-abbreviation])
                      (if (= months 1)
                        (str " " (lower-case (tr {:fallback-phrase "month"} [:time :month])))
                        (str " " (lower-case (tr {:fallback-phrase "months"} [:time :months])))))
        days-text (if short?
                    (tr {:fallback-phrase "d"} [:time :day-abbreviation])
                    (if (= days 1)
                      (str " " (lower-case (tr {:fallback-phrase "day"} [:time :day])))
                      (str " " (lower-case (tr {:fallback-phrase "days"} [:time :days])))))
        hours-text (if short?
                     (tr {:fallback-phrase "h"} [:time :hour-abbreviation])
                     (if (= hours 1)
                       (str " " (lower-case (tr {:fallback-phrase "hour"} [:time :hour])))
                       (str " " (lower-case (tr {:fallback-phrase "hours"} [:time :hours])))))
        minutes-text (if short?
                       (tr {:fallback-phrase "m"} [:time :minute-abbreviation])
                       (if (= minutes 1)
                         (str " " (lower-case (tr {:fallback-phrase "minute"} [:time :minute])))
                         (str " " (lower-case (tr {:fallback-phrase "minutes"} [:time :minutes])))))
        seconds-text (if short?
                       (tr {:fallback-phrase "s"} [:time :second-abbreviation])
                       (if (= seconds 1)
                         (str " " (lower-case (tr {:fallback-phrase "second"} [:time :second])))
                         (str " " (lower-case (tr {:fallback-phrase "seconds"} [:time :seconds])))))]
    (s/join (or separator " ")
      (filter #(not (s/blank? %))
        (conj []
          (when (or (:month? include-zero) (> months 0)) (str negative-text months months-text))
          (when (or (:days? include-zero) (> days 0)) (str negative-text days days-text))
          (when (or (:hours? include-zero) (> hours 0)) (str negative-text hours hours-text))
          (when (or (:minutes? include-zero) (> minutes 0)) (str negative-text minutes minutes-text))
          (when (or (:seconds? include-zero) (> seconds 0)) (str negative-text seconds seconds-text)))))))

(defn readable-duration [{:keys [duration-seconds
                                 seconds
                                 separator
                                 short?
                                 include-zero]}]
  (let [seconds (or seconds duration-seconds)
        negative? (neg? seconds)
        int-seconds (int (if negative? (- seconds) seconds))
        days (quot int-seconds 86400)
        days-r (rem int-seconds 86400)
        hours (quot days-r 3600)
        hours-r (rem days-r 3600)
        minutes (quot hours-r 60)
        seconds (rem hours-r 60)]
    (readable-time {:seconds seconds :minutes minutes :hours hours :days days
                    :negative-duration? negative?
                    :short? short? :include-zero include-zero
                    :separator separator})))

(defn readable-instant [{:keys [instant
                                separator
                                short?
                                include-zero]}]
  (let [hours (t/hour instant)
        minutes (t/minute instant)
        seconds (t/second instant)]
    (readable-time {:seconds seconds :minutes minutes :hours hours
                    :short? short? :include-zero include-zero
                    :separator separator})))

(defn format-time-short [{:keys [seconds
                                 minutes
                                 hours]}]
  (cond
    (or (and (not (nil? seconds)) (< seconds 0))
      (and (not (nil? minutes)) (< minutes 0))
      (and (not (nil? hours)) (< hours 0)))
    (tr {:fallback-phrase "Out of time"} [:time :out-of-time])

    (and seconds minutes hours)
    (gstr/format "%02d:%02d:%02d" hours minutes seconds)

    (and minutes hours)
    (gstr/format "%02d:%02d" hours minutes)

    (and seconds minutes)
    (gstr/format "%02d:%02d" minutes seconds)

    seconds
    (gstr/format "%02d" seconds)
    :else (tr {:fallback-phrase "Out of time"} [:time :out-of-time])))

(defn date-breakdown [date]
  {:day   (t/day-of-week date)
   :month (t/month date)
   :dd    (t/day-of-month date)
   :MM    (t/int (t/month date))
   :yyyy  (t/int (t/year date))})

(defn time-breakdown [time]
  {:mm (t/minute time)
   :HH (t/hour time)
   :ss (t/second time)})

(defn instant-breakdown
  "Takes an instant of time and breaks it down into units."
  [t]
  (merge (date-breakdown t)
    (time-breakdown t)))

(defn format-seconds [{:keys [seconds format]}]
  (let [int-seconds (int seconds)
        days-r (rem int-seconds 86400)
        hours (quot days-r 3600)
        hours-r (rem days-r 3600)
        minutes-in-hours (quot hours-r 60)
        seconds-in-minutes (rem hours-r 60)
        format (or format :HH-mm-ss)
        timer {:ss       {:seconds int-seconds}
               :mm-ss    {:seconds seconds-in-minutes
                          :minutes minutes-in-hours}
               :HH-mm-ss {:seconds seconds-in-minutes
                          :minutes minutes-in-hours
                          :hours   hours}}]
    (format-time-short (get timer format))))

(defn- prepend-zero [time-number]
  (if (< time-number 10)
    (str "0" time-number)
    time-number))

(defn format-date [t & [locale]]
  (let [t-break (date-breakdown t)
        month (prepend-zero (:MM t-break))
        day (prepend-zero (:dd t-break))]
    (case locale
      ("en-US", "es-US")
      (str (:yyyy t-break) "." day "." month)
      (str (:yyyy t-break) "." month "." day))))

(defn format-time [t & [locale with-seconds?]]
  (let [t-break (time-breakdown t)
        hour (prepend-zero (:HH t-break))
        minute (prepend-zero (:mm t-break))
        second (when with-seconds? (prepend-zero (:ss t-break)))]
    (str hour ":" minute (when with-seconds? ":") second)))

(defn format-tick-ts [utc-ts & [{:keys [date? locale time-zone with-seconds?]}]]
  (when utc-ts
    (let [locale (or locale @(rf/subscribe [:locale]))
          converted-time (t/in utc-ts (or time-zone ))]
      (if date?
        (format-date converted-time locale)
        (format-time converted-time locale with-seconds?)))))
